/**
 * What the heavy layers draw from, held on the GPU: the land as a texture,
 * film grain, every town and the biggest cities, the coastline, and the
 * particles of the supernova. All of it is built once, again after a lost
 * context comes back, and the particles whenever they are reseeded.
 */
import { cities } from '../data/cities'
import { landRings } from '../data/land'
import type { Gpu } from './gpu'

/** Samples along each particle's trail. */
export const TRAIL_SAMPLES = 6
/** How many of the largest agglomerations get a soft glow of their own. */
const METRO_COUNT = 70
/** The land texture's width at most; it is half as high. */
const LAND_TEXTURE_MAX = 4096
const GRAIN_SIZE = 256

/** The particles of the supernova, from the intro's seeding. */
export interface Particles {
  /** The lights: each flies out from the star to its place on the globe and stops there. */
  readonly shell: {
    count: number
    /** Its place on the unit globe. */
    x: Float32Array
    y: Float32Array
    z: Float32Array
    brightness: Float32Array
    /** The sideways bend of its flight, square to its place. */
    bendX: Float32Array
    bendY: Float32Array
    bendZ: Float32Array
    /** When it leaves after the burst, and how long it takes to arrive (s). */
    departure: Float32Array
    flight: Float32Array
  }
  /** Debris that leaves for good. */
  readonly ejecta: {
    count: number
    /** Its direction. */
    x: Float32Array
    y: Float32Array
    z: Float32Array
    /** How far it gets, how fast it slows, how long it glows. */
    reach: Float32Array
    drag: Float32Array
    life: Float32Array
  }
  /** Per particle, the shell first and then the ejecta. */
  readonly width: Float32Array
  readonly temperature: Float32Array
  readonly twinkle: Float32Array
  readonly phase: Float32Array
  readonly glint: Float32Array
}

export interface GlobeResources {
  /** A unit quad, a coastline segment and a full-screen triangle, as templates. */
  readonly quad: WebGLBuffer
  readonly segment: WebGLBuffer
  readonly screen: WebGLBuffer
  /** A particle's trail as a ribbon of `TRAIL_SAMPLES` pieces, then its head. */
  readonly ribbon: WebGLBuffer
  readonly ribbonVertices: number
  readonly towns: WebGLBuffer
  readonly townCount: number
  readonly metros: WebGLBuffer
  readonly metroCount: number
  readonly coast: WebGLBuffer
  readonly coastSegments: number
  readonly land: WebGLTexture
  readonly grain: WebGLTexture
}

export interface ParticleResources {
  /** Every particle's flight, for the supernova. */
  readonly sparks: WebGLBuffer
  /** The lights where they settled, for the globe on the page. */
  readonly lights: WebGLBuffer
}

export function buildGlobe(gpu: Gpu): GlobeResources {
  const ribbon: number[] = []
  for (let j = 0; j < TRAIL_SAMPLES; j++)
    ribbon.push(
      j,
      1,
      0,
      0,
      j,
      -1,
      0,
      0,
      j + 1,
      1,
      0,
      0,
      j + 1,
      1,
      0,
      0,
      j,
      -1,
      0,
      0,
      j + 1,
      -1,
      0,
      0
    )
  for (const [x, y] of [
    [-1, -1],
    [1, -1],
    [-1, 1],
    [-1, 1],
    [1, -1],
    [1, 1],
  ])
    ribbon.push(0, x, 1, y)

  const towns = new Float32Array(cities.count * 4)
  for (let c = 0; c < cities.count; c++) {
    towns[c * 4] = cities.x[c]
    towns[c * 4 + 1] = cities.y[c]
    towns[c * 4 + 2] = cities.z[c]
    towns[c * 4 + 3] = clamp(0.2 + 0.18 * Math.log(cities.population[c] / 15000), 0.2, 1)
  }

  const largest = Array.from({ length: cities.count }, (_, i) => i)
    .sort((a, b) => cities.population[b] - cities.population[a])
    .slice(0, METRO_COUNT)
  const metros = new Float32Array(largest.length * 4)
  largest.forEach((c, i) => {
    metros[i * 4] = cities.x[c]
    metros[i * 4 + 1] = cities.y[c]
    metros[i * 4 + 2] = cities.z[c]
    metros[i * 4 + 3] = 0.02 + 0.035 * Math.pow(cities.population[c] / 1e7, 0.4)
  })

  // the coastline, segment by segment
  const coast: number[] = []
  for (const { points: p } of landRings) {
    const n = p.length / 3
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n
      coast.push(p[i * 3], p[i * 3 + 1], p[i * 3 + 2], p[j * 3], p[j * 3 + 1], p[j * 3 + 2])
    }
  }

  return {
    quad: gpu.staticBuffer(new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1])),
    segment: gpu.staticBuffer(new Float32Array([0, -1, 1, -1, 0, 1, 0, 1, 1, -1, 1, 1])),
    screen: gpu.staticBuffer(new Float32Array([-1, -1, 3, -1, -1, 3])),
    ribbon: gpu.staticBuffer(new Float32Array(ribbon)),
    ribbonVertices: ribbon.length / 4,
    towns: gpu.staticBuffer(towns),
    townCount: cities.count,
    metros: gpu.staticBuffer(metros),
    metroCount: largest.length,
    coast: gpu.staticBuffer(new Float32Array(coast)),
    coastSegments: coast.length / 6,
    land: landTexture(gpu.gl),
    grain: grainTexture(gpu.gl),
  }
}

/** The particles' buffers, replacing `previous`. */
export function buildParticles(
  gpu: Gpu,
  particles: Particles,
  previous: ParticleResources | null
): ParticleResources {
  const { shell, ejecta } = particles
  const total = shell.count + ejecta.count
  const sparks = new Float32Array(total * 16)
  for (let k = 0; k < total; k++) {
    const o = k * 16
    if (k < shell.count) {
      sparks[o] = shell.x[k]
      sparks[o + 1] = shell.y[k]
      sparks[o + 2] = shell.z[k]
      sparks[o + 3] = shell.departure[k]
      sparks[o + 4] = shell.bendX[k]
      sparks[o + 5] = shell.bendY[k]
      sparks[o + 6] = shell.bendZ[k]
      sparks[o + 7] = shell.flight[k]
      sparks[o + 8] = shell.brightness[k]
    } else {
      const e = k - shell.count
      sparks[o] = ejecta.x[e]
      sparks[o + 1] = ejecta.y[e]
      sparks[o + 2] = ejecta.z[e]
      sparks[o + 3] = ejecta.reach[e]
      sparks[o + 7] = ejecta.drag[e]
      sparks[o + 8] = ejecta.life[e]
      sparks[o + 14] = 1
    }
    sparks[o + 9] = particles.width[k]
    sparks[o + 10] = particles.temperature[k]
    sparks[o + 11] = particles.twinkle[k]
    sparks[o + 12] = particles.phase[k]
    sparks[o + 13] = particles.glint[k]
  }
  const lights = new Float32Array(shell.count * 7)
  for (let k = 0; k < shell.count; k++) {
    const o = k * 7
    lights[o] = shell.x[k]
    lights[o + 1] = shell.y[k]
    lights[o + 2] = shell.z[k]
    lights[o + 3] = shell.brightness[k]
    lights[o + 4] = particles.width[k]
    lights[o + 5] = particles.phase[k]
    lights[o + 6] = particles.glint[k]
  }
  if (previous) {
    gpu.gl.deleteBuffer(previous.sparks)
    gpu.gl.deleteBuffer(previous.lights)
  }
  return { sparks: gpu.staticBuffer(sparks), lights: gpu.staticBuffer(lights) }
}

/** The land as an equirectangular alpha map, for the planet to look up per pixel. */
function landTexture(gl: WebGLRenderingContext) {
  const width = Math.min(LAND_TEXTURE_MAX, gl.getParameter(gl.MAX_TEXTURE_SIZE))
  const height = width / 2
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const g = canvas.getContext('2d')!
  function X(lon: number) {
    return ((lon + 180) / 360) * width
  }
  function Y(lat: number) {
    return ((90 - lat) / 180) * height
  }
  g.fillStyle = '#fff'
  g.beginPath()
  for (const { lonLat: ll } of landRings) {
    // unwrap the longitudes so the ring never jumps across the date line
    const n = ll.length / 2
    const U: number[] = []
    let lon = ll[0]
    let prev = ll[0]
    for (let i = 0; i < n; i++) {
      let d = ll[i * 2] - prev
      d -= Math.round(d / 360) * 360
      lon = i ? lon + d : ll[0]
      prev = ll[i * 2]
      U.push(lon, ll[i * 2 + 1])
    }
    let dc = ll[0] - prev
    dc -= Math.round(dc / 360) * 360
    const endLon = lon + dc
    const wraps = Math.abs(endLon - ll[0]) > 180 // goes all the way round a pole
    for (let off = -360; off <= 360; off += 360) {
      g.moveTo(X(U[0] + off), Y(U[1]))
      for (let i = 1; i < n; i++) g.lineTo(X(U[i * 2] + off), Y(U[i * 2 + 1]))
      if (wraps) {
        const pole = U[1] < 0 ? -90 : 90
        g.lineTo(X(endLon + off), Y(U[1]))
        g.lineTo(X(endLon + off), Y(pole))
        g.lineTo(X(U[0] + off), Y(pole))
      }
      g.closePath()
    }
  }
  g.fill()
  const texture = gl.createTexture()
  gl.bindTexture(gl.TEXTURE_2D, texture)
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false)
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.ALPHA, gl.ALPHA, gl.UNSIGNED_BYTE, canvas)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
  canvas.width = canvas.height = 0
  return texture
}

/** Noise round mid-grey, tiled over the screen. */
function grainTexture(gl: WebGLRenderingContext) {
  const noise = new Uint8Array(GRAIN_SIZE * GRAIN_SIZE)
  for (let i = 0; i < noise.length; i++) noise[i] = (118 + Math.random() * 74) | 0
  const texture = gl.createTexture()
  gl.bindTexture(gl.TEXTURE_2D, texture)
  gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1)
  gl.texImage2D(
    gl.TEXTURE_2D,
    0,
    gl.LUMINANCE,
    GRAIN_SIZE,
    GRAIN_SIZE,
    0,
    gl.LUMINANCE,
    gl.UNSIGNED_BYTE,
    noise
  )
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT)
  return texture
}

function clamp(v: number, lo: number, hi: number) {
  return v < lo ? lo : v > hi ? hi : v
}
